import { ReactNode, useState, useRef, useEffect } from "react";
import { SxProps, Typography } from "@mui/material";
import { Box } from "@mui/material";
import { SidePanel } from "./BookingSidePanel";
import { BookingSlot, ExternalMeetingInfo } from "../../../store/schedule/types";
import { TemplateVars } from "@CabComponents/CabTextTokenInput";
import { CabTextTokenDisplay } from "@CabComponents/CabTextTokenDisplay";
import { CabButton } from "@CabComponents/CabButton";
import { IoArrowBack } from "react-icons/io5";
import { CabIcon } from "@CabComponents/CabIcon";
import { CabTooltip } from "@CabComponents/CabTooltip";
import CabSpinner from "@CabComponents/CabSpinner";
import { MeetingBookingStep } from "../BookMeeting";
import colors from "../../../colors";
import { PROVIDER_BY_ID } from "../../../constants";
import { replaceTemplateVariables } from "../../../utils/scheduleUtils";
import { NAVY_LOGO_SRC } from "../../../resourceUrls";

const BookMeetingWrapper = ({
  showSidePanel, loadingPage, meetingInfo, meetingTitle, meetingDescription, durationText, isAuthenticated,
  templateVars, sx, header, children, slotSelected, onBackButtonClick, step, isPreview, isBooking,
  formState, onSubmit, submitLabel, submitDisabled, submitLoading, showBackButton, showSubmitButton, logo,
  showCabinetLogoInSidePanel, hideFooter, name, questionAnswers, hideTitleDescription, stickyFooter,
  showCabinetLogo
}: {
  showSidePanel: boolean,
  loadingPage: boolean,
  meetingInfo: ExternalMeetingInfo,
  meetingTitle: string,
  meetingDescription: string,
  durationText: string,
  isAuthenticated: boolean,
  templateVars: TemplateVars,
  sx: SxProps,
  header: ReactNode,
  children: ReactNode,
  slotSelected?: BookingSlot,
  onBackButtonClick?: () => void,
  step: MeetingBookingStep,
  isPreview?: boolean,
  isBooking?: boolean,
  formState?: { isValid: boolean },
  onSubmit?: () => void,
  submitLabel?: string,
  submitDisabled?: boolean,
  submitLoading?: boolean,
  showBackButton?: boolean,
  showSubmitButton?: boolean,
  logo?: string,
  showCabinetLogoInSidePanel?: boolean,
  hideFooter?: boolean,
  stickyFooter?: boolean,
  name?: string,
  questionAnswers?: {
    questionId: number,
    question: string,
    answer: string,
  }[],
  hideTitleDescription?: boolean,
  showCabinetLogo?: boolean,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (descriptionRef.current) {
        const element = descriptionRef.current;
        setIsTruncated(element.scrollHeight > element.clientHeight);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [meetingDescription]);

  return (
    <Box sx={{ display: 'flex', width: '100%', ...sx }}>
      {showSidePanel && (
        <SidePanel
          logo={logo}
          showCabinetLogo={showCabinetLogoInSidePanel}
          showSkeletons={loadingPage}
          name={meetingInfo ? (
            <Box>
              <CabTextTokenDisplay
                chipSx={{fontSize: '22px', padding: '12px'}}
                templateVars={templateVars}
              >
                {meetingTitle || meetingInfo.title_booked || meetingInfo.title}
              </CabTextTokenDisplay>
            </Box>
          ) : (
            <></>
          )}
          description={meetingInfo ? (
            <Box>
              <CabTextTokenDisplay
                chipSx={{fontSize: '14px', padding: '10px'}}
                templateVars={templateVars}
              >
                {meetingDescription}
              </CabTextTokenDisplay>
            </Box>
          ) : (
            <></>
          )}
          durationText={durationText}
          locations={meetingInfo?.locations?.map((l, index) => (
            <CabTextTokenDisplay
              key={index}
              templateVars={templateVars}
            >
              {replaceTemplateVariables(l, name || '', questionAnswers || [])}
            </CabTextTokenDisplay>
          ))}
          conferences={meetingInfo.conference ? [{
            provider: meetingInfo.conference.provider,
            label: PROVIDER_BY_ID[meetingInfo.conference.provider].label
          }] : []}
          leaders={meetingInfo?.leaders || []}
          meetingId={isAuthenticated ? meetingInfo?.id : undefined}
          slotSelected={slotSelected}
        />
      )}
      <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {!showSidePanel && !hideTitleDescription && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: 4,
              paddingBottom: 0
            }}>
            <Typography variant="h5" sx={{
              fontWeight: 700
            }}>
              <CabTextTokenDisplay
                chipSx={{fontSize: '22px', padding: '12px'}}
                templateVars={templateVars}
              >
                {meetingTitle || meetingInfo.title_booked || meetingInfo.title}
              </CabTextTokenDisplay>
            </Typography>
            <Box>
              <Typography
                ref={descriptionRef}
                variant="body1"
                sx={{
                  overflow: isExpanded ? 'auto' : 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: isExpanded ? 5 : 2,
                  WebkitBoxOrient: 'vertical',
                  maxHeight: isExpanded ? 'calc(1.5em * 5)' : '3em',
                  transition: 'max-height 0.3s ease-in-out',
                  lineHeight: '1.5em',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: colors.black100,
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: colors.black300,
                    borderRadius: '3px',
                    '&:hover': {
                      background: colors.black400,
                    },
                  },
                }}
              >
                <CabTextTokenDisplay
                  chipSx={{fontSize: '14px', padding: '10px'}}
                  templateVars={templateVars}
                >
                  {meetingDescription}
                </CabTextTokenDisplay>
              </Typography>
              {isTruncated && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    mt: 1
                  }}
                >
                  <CabButton
                    buttonType="text"
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{ 
                      padding: 0,
                      fontSize: '14px',
                      color: colors.lavendar600,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {isExpanded ? 'Show Less' : 'View All'}
                  </CabButton>
                </Box>
              )}
            </Box>
          </Box>
        )}
        <Box>{header}</Box>
        <Box sx={{
          flex: 1
        }}>{children}</Box>
        {!hideFooter && (
          <Box sx={stickyFooter ? {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderTop: `1px solid ${colors.black100}`,
            width: '100%',
            zIndex: 1,
          } : {}}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                padding: 4,
                paddingTop: 3,
                paddingBottom: 3,
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}>
              {showBackButton && onBackButtonClick && (
                <CabButton
                  buttonType="text"
                  icon={<CabIcon Icon={IoArrowBack} color={colors.lavendar600} />}
                  onClick={onBackButtonClick}
                >
                  Back
                </CabButton>
              )}
              {showSubmitButton && onSubmit && (
                <CabTooltip
                  title={isPreview ? 'This is just a preview. When your invited participant clicks this button, ' +
                  'Cabinet will schedule the meeting on your behalf.' : ''}
                  placement='top-start'
                  wrapWithSpan
                >
                  <Box>
                    <CabButton 
                      color='primary'
                      onClick={onSubmit}
                      disabled={submitDisabled || (formState && !formState.isValid)}
                    >
                      {submitLoading || isBooking
                        ? <CabSpinner scale={1} sx={{ paddingLeft: 5, paddingRight: 5 }} />
                        : submitLabel || 'Next'}
                    </CabButton>
                    {isPreview && (
                      <Typography color={colors.black700} sx={{
                        marginTop: 1
                      }}>
                        (This is a preview, the meeting cannot be booked from here)
                      </Typography>
                    )}
                  </Box>
                </CabTooltip>
              )}
            </Box>
          </Box>
        )}
        {showCabinetLogo && (
          <Box
            sx={{
              paddingBottom: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              alignItems: 'center'
            }}>
            <Typography variant="caption" sx={{
              fontSize: 12
            }}>Powered by</Typography>
            <Box component='img' src={NAVY_LOGO_SRC} alt='Cabinet' sx={{
              width: 110
            }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BookMeetingWrapper;
